import React, { useEffect } from "react";
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    getGroupedRowModel,
    SortingState,
    getExpandedRowModel,
    VisibilityState,
} from "@tanstack/react-table";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-light-svg-icons";
import {
    faChevronsRight,
    faChevronLeft,
    faChevronRight,
    faChevronsLeft,
} from "@fortawesome/pro-regular-svg-icons";
import EasyListBox from "./EasyListBox";

const Table = ({
    data,
    filters,
    setFilters,
    fetching,
    columns,
    pages,
    showPageSize = pages,
    showGoToPage = pages,
    pageSize,
    setPageSize,
    maxPages,
    orderBy,
    setOrderBy,
    bodyMaxHeight,
    showFilters = null,
    setShowFilters,
    isFetching,
    exportFunc,
    grouping,
    setGrouping,
    totalElements,
    exportCSVOnly = false,
    title = null,
    description = null,
    scroll = true,
    setScrollY,
    visibility,
    name,
    thresholdColors,
    showPlaceholder = true,
    onDragStart,
    onDragEnd,
    mayBecomeDraggable = false,
    handleDrop,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    reload,
}) => {
    // useEffect(() => {
    //     table.getAllColumns().map((column) => {
    //         if (window.innerWidth < 1200) {
    //             if (column.id === "answ/miss/abd_rate") {
    //                 column.toggleVisibility(true);
    //             }
    //             if (
    //                 ["answered_rate", "missed_rate", "abandoned_rate"].includes(
    //                     column.id
    //                 )
    //             ) {
    //                 column.toggleVisibility(false);
    //             }
    //         } else {
    //             if (column.id === "answ/miss/abd_rate") {
    //                 column.toggleVisibility(false);
    //             }
    //             if (
    //                 ["answered_rate", "missed_rate", "abandoned_rate"].includes(
    //                     column.id
    //                 )
    //             ) {
    //                 column.toggleVisibility(true);
    //             }
    //         }
    //     });
    //     const handleResize = (e) => {
    //         table.getAllColumns().map((column) => {
    //             if (column?.getCanHide()) {
    //                 if (e.target.innerWidth < 1200) {
    //                     if (column.id === "answ/miss/abd_rate") {
    //                         column.toggleVisibility(true);
    //                     }
    //                     if (
    //                         [
    //                             "answered_rate",
    //                             "missed_rate",
    //                             "abandoned_rate",
    //                         ].includes(column.id)
    //                     ) {
    //                         column.toggleVisibility(false);
    //                     }
    //                 } else {
    //                     if (column.id === "answ/miss/abd_rate") {
    //                         column.toggleVisibility(false);
    //                     }
    //                     if (
    //                         [
    //                             "answered_rate",
    //                             "missed_rate",
    //                             "abandoned_rate",
    //                         ].includes(column.id)
    //                     ) {
    //                         column.toggleVisibility(true);
    //                     }
    //                 }
    //             }
    //         });
    //     };
    //     window.addEventListener("resize", handleResize);
    //     return () => {
    //         window.removeEventListener("resize", handleResize);
    //     };
    // }, []);

    const tableData = React.useMemo(
        () =>
            !isFetching && data
                ? data
                : isFetching
                ? Array(pageSize ? pageSize : 10).fill({})
                : {},
        [isFetching, data, pageSize]
    );

    const tableColumns = React.useMemo(
        () =>
            isFetching
                ? columns.map((column) => ({
                      ...column,
                      cell: () => (
                          <div role="status" className="max-w-sm animate-pulse">
                              <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
                          </div>
                      ),
                  }))
                : columns,
        [isFetching, columns]
    );

    const table = useReactTable({
        data: tableData,
        columns: tableColumns,
        state: {
            sorting: orderBy,
            grouping: grouping,
            columnVisibility: visibility,
        },
        onGroupingChange: setGrouping,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
        onSortingChange: setOrderBy,
        enableColumnResizing: true,
        columnResizeMode: "onChange",
        getExpandedRowModel: grouping ? getExpandedRowModel() : null,
        getGroupedRowModel: grouping ? getGroupedRowModel() : null,
    });

    const handleFilters = (filter) => {
        setFilters((oldFilters) => {
            return { ...oldFilters, ...filter };
        });
    };

    const handleScroll = (e) => {
        const { scrollTop } = e.target;
        if (setScrollY) {
            setScrollY(scrollTop);
        }
    };

    return (
        <span
            className={"rounded-lg bg-white" + (pages ? " shadow-table" : "")}
        >
            {(title || (pages && showPageSize) || exportFunc) && (
                <div className="flex flex-col items-end justify-between gap-2 p-2 pt-1 md:flex-row">
                    <div className="relative flex items-center justify-between gap-5">
                        <div className="flex flex-col gap-1">
                            {title && (
                                <div className="text-2xl font-bold text-gray-800 dark:text-white">
                                    {title}
                                </div>
                            )}
                            {description && (
                                <div className="text-sm text-gray-800 dark:text-white">
                                    {description}
                                </div>
                            )}
                        </div>
                        {reload && (
                            <button
                                type="button"
                                onClick={() => {
                                    reload();
                                }}
                                className="linear bg-transparent rounded-[8px] border border-brand-500 px-[10px] py-[4px] text-sm font-bold uppercase text-brand-500 transition duration-200 ms-4 hover:bg-brand-600 hover:text-white active:bg-brand-700 active:text-white dark:bg-brand-400 dark:text-brand-500 dark:hover:bg-brand-300 dark:hover:text-white dark:active:bg-brand-200 dark:active:text-white"
                            >
                                Ricarica
                            </button>
                        )}
                    </div>
                    {pages && showPageSize && (
                        <span className="flex justify-end">
                            <span className="flex items-center gap-1 p-2">
                                {"Mostra"}
                            </span>
                            <EasyListBox
                                data={[
                                    { value: 10, label: "10" },
                                    { value: 20, label: "20" },
                                    { value: 30, label: "30" },
                                ]}
                                handleSelection={setPageSize}
                                selected={pageSize ? pageSize : 10}
                            />
                        </span>
                    )}
                    {exportFunc && <span className="ml-auto flex"></span>}
                </div>
            )}
            <div
                onScroll={handleScroll}
                className={joinClassNames(
                    scroll ? "overflow-y-auto" : "",
                    "scrollbar-track-gray-50 scrollbar-thin scrollbar-thumb-brand-50 scrollbar-track-rounded-lg scrollbar-thumb-rounded-lg mt-4 bg-white dark:bg-gray-800"
                )}
                style={{
                    maxHeight: bodyMaxHeight
                        ? bodyMaxHeight
                        : scroll
                        ? "60vh"
                        : "100%",
                }}
            >
                <table
                    className={
                        name +
                        " w-full table-auto bg-white text-left text-sm text-gray-500 rtl:text-right dark:bg-gray-800 dark:text-white"
                    }
                >
                    <thead
                        className={joinClassNames(
                            scroll ? "sticky" : "",
                            "dark:border-magenta top-0 z-[1] border-b border-[#F5F6FA] bg-white text-xs uppercase text-gray-700 dark:bg-gray-800 dark:text-white"
                        )}
                    >
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    if (
                                        header?.column?.id === "queue_extension"
                                    ) {
                                        if (
                                            header.column.getCanGroup() &&
                                            grouping
                                        ) {
                                            if (!header.column.getIsGrouped()) {
                                                header.column.toggleGrouping();
                                            }
                                        }
                                    }
                                    return (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            className={
                                                (header?.column?.columnDef?.meta
                                                    ?.columnClasses ?? "") +
                                                " px-2 py-1"
                                            }
                                            style={{
                                                position: "relative",
                                                width: header.getSize(),
                                                maxWidth: header.column
                                                    .columnDef.meta?.maxWidth
                                                    ? header.column.columnDef
                                                          .meta?.maxWidth
                                                    : null,
                                            }}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    {...{
                                                        className:
                                                            header.column.getCanSort() &&
                                                            orderBy
                                                                ? "cursor-pointer select-none flex-row flex"
                                                                : "",
                                                        onClick:
                                                            header.column.getToggleSortingHandler(),
                                                    }}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext()
                                                    )}
                                                    {/* {header.column.getCanGroup() &&
                          header.column.id === "queue_extension" ? (
                            // If the header can be grouped, let's add a toggle
                            <button
                              {...{
                                onClick:
                                  header.column.getToggleGroupingHandler(),
                                style: {
                                  cursor: "pointer",
                                },
                              }}
                            >
                              {header.column.getCanGroup() && grouping && (
                                <>
                                  &nbsp;
                                  {header.column.getIsGrouped() ? (
                                    <FontAwesomeIcon
                                      size="lg"
                                      icon={faArrowsFromLine}
                                    />
                                  ) : (
                                    <FontAwesomeIcon
                                      size="lg"
                                      icon={faArrowsToLine}
                                    />
                                  )}
                                </>
                              )}
                            </button>
                          ) : null} */}
                                                    {{
                                                        asc: (
                                                            <FiChevronUp
                                                                className="text-table-header dark:text-magenta mr-2 h-5 w-5"
                                                                style={{
                                                                    marginBottom:
                                                                        -3,
                                                                    paddingBottom: 3,
                                                                }}
                                                            />
                                                        ),
                                                        desc: (
                                                            <FiChevronDown
                                                                className="text-table-header dark:text-magenta mr-2 h-5 w-5"
                                                                style={{
                                                                    marginBottom:
                                                                        -3,
                                                                    paddingBottom: 3,
                                                                }}
                                                            />
                                                        ),
                                                    }[
                                                        header.column.getIsSorted()
                                                    ] ?? null}
                                                </div>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length > 0 ? (
                            table.getRowModel().rows.map((row) => {
                                return (
                                    <tr
                                        className="h-[22px]"
                                        draggable={
                                            mayBecomeDraggable &&
                                            !row?.original?.number?.includes(
                                                "webconf"
                                            )
                                                ? row?.original?.state?.answered
                                                    ? true
                                                    : false
                                                : false
                                        }
                                        key={row.id}
                                        onDragStart={(e) => onDragStart(e, row)}
                                        onDragEnd={onDragEnd}
                                        onDragOver={
                                            (row?.original?.state?.answered ||
                                                row?.original?.status ===
                                                    "Talk") &&
                                            !row?.original?.number?.includes(
                                                "webconf"
                                            )
                                                ? handleDragOver
                                                : null
                                        }
                                        onDrop={(e) => {
                                            handleDrop(e, row);
                                        }}
                                        onDragLeave={(e) =>
                                            handleDragLeave(
                                                e,
                                                row?.original?.id
                                            )
                                        }
                                        onDragEnter={(e) => {
                                            handleDragEnter(
                                                e,
                                                row?.original?.id
                                            );
                                        }}
                                        style={{
                                            backgroundColor: row?.original
                                                ?.call_uuid
                                                ? "red"
                                                : row?.original?.queue_extension
                                                ? row?.original
                                                      ?.queue_extension ===
                                                  "total"
                                                    ? "#E9ECEF"
                                                    : row?.original
                                                          ?.queue_extension &&
                                                      row.getIsGrouped()
                                                    ? "#E9ECEF"
                                                    : null
                                                : row?.original?.state
                                                      ?.flipHoldTime
                                                ? row?.original?.state
                                                      ?.flipHoldTime === 1
                                                    ? thresholdColors?.[0] ??
                                                      "#FCA5A5b3"
                                                    : row?.original?.state
                                                          ?.flipHoldTime === 2
                                                    ? thresholdColors?.[1] ??
                                                      "#F87171b3"
                                                    : row?.original?.state
                                                          ?.flipHoldTime === 3
                                                    ? thresholdColors?.[2] ??
                                                      "#F53939b3"
                                                    : null
                                                : row?.original?.type
                                                ? row?.original?.type ===
                                                  "total"
                                                    ? "#E9ECEF"
                                                    : null
                                                : row?.original?.stat_date
                                                ? row?.original?.stat_date ===
                                                  "total"
                                                    ? "#E9ECEF"
                                                    : null
                                                : null,
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td
                                                    key={cell.id}
                                                    style={{
                                                        width: cell.column.getSize(),
                                                        maxWidth: cell.column
                                                            .columnDef.meta
                                                            ?.maxWidth
                                                            ? cell.column
                                                                  .columnDef
                                                                  .meta
                                                                  ?.maxWidth
                                                            : null,
                                                    }}
                                                    className={
                                                        (cell?.column?.columnDef
                                                            ?.meta
                                                            ?.columnClasses ??
                                                            "") + " p-2"
                                                    }
                                                    draggable={
                                                        cell.column.columnDef
                                                            .meta
                                                            ?.undraggable ===
                                                        true
                                                            ? true
                                                            : false
                                                    }
                                                    onDragStart={(event) => {
                                                        if (
                                                            cell.column
                                                                .columnDef.meta
                                                                ?.undraggable ===
                                                            true
                                                        ) {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                        }
                                                    }}
                                                >
                                                    {cell.getIsGrouped() ? (
                                                        // If it's a grouped cell, add an expander and row count

                                                        <button
                                                            {...{
                                                                onClick:
                                                                    row.getToggleExpandedHandler(),
                                                                style: {
                                                                    cursor: row.getCanExpand()
                                                                        ? "pointer"
                                                                        : "normal",
                                                                    display:
                                                                        "flex",
                                                                    flexDirection:
                                                                        "row",
                                                                },
                                                            }}
                                                        >
                                                            {row.getIsExpanded() ? (
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faChevronDown
                                                                    }
                                                                />
                                                            ) : (
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faChevronRight
                                                                    }
                                                                />
                                                            )}
                                                            {flexRender(
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                                cell.getContext()
                                                            )}
                                                            &nbsp;(
                                                            {row.subRows.length}
                                                            )
                                                        </button>
                                                    ) : cell.getIsAggregated() ? (
                                                        // If the cell is aggregated, use the Aggregated
                                                        // renderer for cell
                                                        flexRender(
                                                            cell.column
                                                                .columnDef
                                                                .aggregatedCell ??
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                            cell.getContext()
                                                        )
                                                    ) : cell.getIsPlaceholder() ? null : (
                                                        flexRender(
                                                            cell.column
                                                                .columnDef.cell,
                                                            cell.getContext()
                                                        )
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr className="dark:from-magenta bg-size-200 bg-pos-0 w-full bg-gradient-to-t from-brand-50  via-white to-white transition-all duration-300 dark:via-gray-800 dark:to-gray-800">
                                {showPlaceholder && (
                                    <td
                                        className="mx-auto p-4 text-center text-lg"
                                        colSpan={15}
                                    >
                                        {"Nessun dato"}
                                    </td>
                                )}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {pages && (
                <div className="text-table-navigation flex flex-col justify-between gap-2 border-t border-[#F5F6FA] p-3 md:flex-row md:items-center">
                    {showGoToPage && data && totalElements ? (
                        <span>
                            <p className="pt-2 text-xs md:ps-3">
                                visualizzati{" "}
                                {`${pageSize * (filters?.page - 1) + 1} - ${
                                    pageSize * (filters?.page - 1) +
                                    data?.length
                                } di ${totalElements}`}
                            </p>
                        </span>
                    ) : (
                        <span></span>
                    )}
                    <div className="flex flex-col gap-4 pt-1 md:flex-row">
                        {showGoToPage ? (
                            <div className="hidden flex-row me-2 md:flex">
                                <p className="self-center text-xs">
                                    {"Vai a pagina:"}
                                </p>
                                <input
                                    className="border-input-border h-[24px] max-h-[24px] w-[48px] max-w-[48px] rounded-lg border text-center text-xs ms-1"
                                    onKeyDown={(e) => {
                                        if (e?.key === "Enter") {
                                            let v = e?.target?.value;
                                            const page =
                                                v === ""
                                                    ? v
                                                    : !Number.isNaN(Number(v))
                                                    ? Number(v)
                                                    : filters?.page;
                                            if (
                                                page !== filters.page &&
                                                page <= maxPages &&
                                                page > 0
                                            ) {
                                                handleFilters({ page: page });
                                            }
                                        }
                                    }}
                                    defaultValue={filters?.page}
                                />
                            </div>
                        ) : (
                            <span></span>
                        )}
                        <div className="flex flex-row space-x-1 text-xs md:self-center">
                            <button
                                className={joinClassNames(
                                    "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray",
                                    filters?.page < 2
                                        ? "opacity-[50%]"
                                        : "hover:bg-brand-primary-10 hover:border-brand-primary-10 hover:text-brand-400"
                                )}
                                onClick={() => {
                                    handleFilters({ page: 1 });
                                }}
                                disabled={filters?.page < 2}
                            >
                                <FontAwesomeIcon
                                    icon={faChevronsLeft}
                                ></FontAwesomeIcon>
                            </button>
                            <button
                                className={joinClassNames(
                                    "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray",
                                    filters?.page < 2
                                        ? "opacity-[50%]"
                                        : "hover:bg-brand-primary-10 hover:border-brand-primary-10 hover:text-brand-400"
                                )}
                                onClick={() => {
                                    handleFilters({ page: filters?.page - 1 });
                                }}
                                disabled={filters?.page < 2}
                            >
                                <FontAwesomeIcon
                                    icon={faChevronLeft}
                                ></FontAwesomeIcon>
                            </button>
                            {filters?.page > 1 && (
                                <button
                                    className={joinClassNames(
                                        "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray",
                                        "break-all leading-none hover:bg-gray-100 hover:text-brand-400"
                                    )}
                                    onClick={() => {
                                        handleFilters({ page: 1 });
                                    }}
                                >
                                    {"1"}
                                </button>
                            )}
                            {filters?.page > 3 && (
                                <button
                                    className={joinClassNames(
                                        "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray"
                                    )}
                                    onClick={() => {}}
                                    disabled={true}
                                >
                                    {"..."}
                                </button>
                            )}
                            {filters?.page > 2 && (
                                <button
                                    className={joinClassNames(
                                        "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray",
                                        "break-all leading-none hover:bg-gray-100 hover:text-brand-400",
                                        (filters?.page - 1).toString().length >
                                            3
                                            ? "text-[8px]"
                                            : ""
                                    )}
                                    onClick={() => {
                                        handleFilters({
                                            page: filters?.page - 1,
                                        });
                                    }}
                                >
                                    {filters?.page - 1}
                                </button>
                            )}
                            <button
                                className={joinClassNames(
                                    "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray",
                                    "break-all leading-none text-brand-400 hover:bg-gray-100",
                                    (filters?.page).toString().length > 3
                                        ? "text-[8px]"
                                        : ""
                                )}
                                onClick={() => {}}
                            >
                                {filters?.page}
                            </button>

                            {filters?.page < maxPages && (
                                <button
                                    className={joinClassNames(
                                        "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray",
                                        "break-all leading-none hover:bg-gray-100 hover:text-brand-400",
                                        (filters?.page + 1).toString().length >
                                            3
                                            ? "text-[8px]"
                                            : ""
                                    )}
                                    onClick={() => {
                                        handleFilters({
                                            page: filters?.page + 1,
                                        });
                                    }}
                                >
                                    {filters?.page + 1}
                                </button>
                            )}
                            {filters?.page < maxPages - 2 && (
                                <button
                                    className={joinClassNames(
                                        "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray"
                                    )}
                                    onClick={() => {}}
                                    disabled={true}
                                >
                                    {"..."}
                                </button>
                            )}
                            {filters?.page < maxPages - 1 && (
                                <button
                                    className={joinClassNames(
                                        "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray",
                                        "break-all leading-none hover:bg-gray-100 hover:text-brand-400",
                                        maxPages.toString().length > 3
                                            ? "text-[8px]"
                                            : ""
                                    )}
                                    onClick={() => {
                                        handleFilters({ page: maxPages });
                                    }}
                                >
                                    {maxPages}
                                </button>
                            )}
                            {/* <span className="flex px-4 text font-thin">
              {filters?.page}/{maxPages}
            </span> */}
                            <button
                                className={joinClassNames(
                                    "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray",
                                    filters?.page >= maxPages
                                        ? "opacity-[50%]"
                                        : "hover:bg-brand-primary-10 hover:border-brand-primary-10 hover:text-brand-400"
                                )}
                                onClick={() => {
                                    handleFilters({ page: filters?.page + 1 });
                                }}
                                disabled={filters?.page >= maxPages}
                            >
                                <FontAwesomeIcon
                                    icon={faChevronRight}
                                ></FontAwesomeIcon>
                            </button>
                            <button
                                className={joinClassNames(
                                    "border-input-border aspect-square h-[24px] max-h-[24px] min-h-[24px] w-[24px] min-w-[24px] max-w-[24px] rounded-lg border pt-0.5 text-[11px] text-pagination-gray",
                                    filters?.page >= maxPages
                                        ? "opacity-[50%]"
                                        : "hover:bg-brand-primary-10 hover:border-brand-primary-10 hover:text-brand-400"
                                )}
                                onClick={() => {
                                    handleFilters({ page: maxPages });
                                }}
                                disabled={filters?.page >= maxPages}
                            >
                                <FontAwesomeIcon
                                    icon={faChevronsRight}
                                ></FontAwesomeIcon>
                            </button>
                        </div>
                    </div>

                    {/* {fetching ? "caricamento" : null} */}
                </div>
            )}
        </span>
    );
};

export default Table;

function joinClassNames(...classes) {
    return classes.filter(Boolean).join(" ");
}
